/* global pendo */

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import React, { Suspense } from 'react';
import classnames from 'classnames';
import { Redirect, Route, withRouter } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import Validate from './components/shared/auth/Validate';
import ProtectedRoute from './components/ProtectedRoute';
import Logout from './components/shared/Logout';
import { connect } from 'react-redux';
import {
  DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN,
  DATADOG_SITE,
  getTalkjsRole,
  talkjs_app_id,
} from './config/Config';
import {
  fetchConversations,
  fetchPersistedUser,
  createTalkJSSession,
  updateUnreadChats,
  fetchAllProposals,
  saveDataRefreshInterval,
  logoutUserAction,
  ghostUser,
  fetchPermissionsAction,
} from './actions';
import './App.scss';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FeatureFlagContext,
  FeatureFlagProvider,
} from 'v2/context/featureFlagContext';
import { QueryClient, QueryClientProvider, setLogger } from 'react-query';
import { withAuth0 } from '@auth0/auth0-react';
import Inactive from 'v2/Inactive/Inactive';
import SignUpInvite from 'v2/pages/SignUpInvite/SignUpInvite';
import Layout from 'v2/common/Layout/Layout';
import SearchViewContainer from 'v2/pages/SearchView/SearchViewContainer';
import DataVendors from 'v2/pages/DataVendors/DataVendors';
import { Spinner } from 'react-bootstrap';
import SearchDetailsContainer from 'v2/pages/SearchDetails/SearchDetailsContainer';
import DataVendorDetailsContainer from 'v2/pages/DataVendorDetails/DataVendorDetailsContainer';
import ContactsDetailsContainer from 'v2/pages/ContactsDetails/ContactsDetailsContainer';
import Contacts from 'v2/pages/Contacts/Contacts';
import DatasetDetailsContainer from 'v2/pages/DrmFeatures/DataSets/DataSetDetails/DatasetDetailsContainer';
import Settings from 'v2/pages/Settings/Settings';
import CreateSearchV2 from 'v2/pages/CreateSearch/CreateSearch';
import SignUpFlow from 'v2/pages/SignUpFlow/SignUpFlow';
import Waitlist from 'v2/pages/Waitlist/Waitlist';
import DataRequests from 'v2/pages/DataRequests/DataRequests';
import { NotificationProvider } from 'v2/context/notificationContext';
import FileLibrary from 'v2/pages/FileLibrary/FileLibrary';
import Discussions from 'v2/pages/Discussions/Discussions';
import {
  getDefaultUrlPerRole,
  hasRequestsPermission,
  isRequestViewerOnly,
} from 'v2/utils/user';
import Users from 'v2/pages/Users/Users';
import GhostButton from 'v2/common/GhostButton/GhostButton';
import TopProviders from 'v2/pages/TopProviders/TopProviders';
import TokenResponseProposal from 'v2/pages/TokenResponseProposal/TokenResponseProposal';
import ProposalResponseToken from 'v2/pages/ProposalResponseToken/ProposalResponseToken';
import AcceptProposal from 'v2/pages/AcceptProposal/AcceptProposal';
import { isPublicRoute } from 'v2/utils/routeUtils';
import EditSearch from 'v2/pages/EditSearch/EditSearch';
import './v2/styles/main.scss';
import { hasPermission, PERMISSION_NAME } from 'v2/utils/permissions';
import DataVendorsHome from 'v2/pages/DataVendors/DataVendorsHome';
import DataSets from 'v2/pages/DrmFeatures/DataSets/DataSets';
import AISearchContainer from 'v2/pages/DrmFeatures/AISearch/AISearchContainer';
import { FEATURE_FLAGS } from 'utils/constants';
import { FeatureToggle } from 'react-feature-toggles';
import CheckUserStatus from 'v2/User/CheckUserStatus';
import { initPushNotification } from 'v2/utils/pushNotification';
import FeaturedDataProvidersContainer from 'v2/pages/FeaturedDataProviders/FeaturedDataProvidersContainer';
import ConfirmEmailVerification from 'v2/common/EmailVerification/ConfirmEmail.tsx';
import SuccessFreeSearchSplash from 'v2/common/SuccessFreeSearchSplash/SuccessFreeSearchSplash';

const Login = React.lazy(() => import('v2/pages/Login/Login'));
const MainWrapper = React.lazy(() => import('./MainWrapper'));

import { loadBackendTranslations } from 'translations';
import DataRequestDetailsContainer from 'v2/pages/DataRequests/DataRequestDetails/DataRequestDetailsContainer';

// Disable logs to console for react-query package
setLogger({
  error: console.log,
});
const queryClient = new QueryClient();
class App extends React.Component {
  constructor(props) {
    super(props);
    this.renderDirectAuth0 = this.renderDirectAuth0.bind(this);
    this.initChat = this.initChat.bind(this);
    this.state = {
      isBusy: true,
      userInitialized: false,
    };
  }

  initChat(user) {
    const { new_user, waitlisted } = user;

    if (new_user || waitlisted || !user.talkjs_id) return;

    Talk.ready.then(() => {
      let userInfo = {
        id: user.talkjs_id,
        name: user.first_name,
        email: user.email,
        role: getTalkjsRole(),
      };

      if (user.data_provider) {
        const photoUrl = user?.avatar?.formats?.thumbnail?.url;
        if (photoUrl && !user.data_provider.hide_identity_allowed)
          userInfo.photoUrl = photoUrl;
      }

      const me = new Talk.User(userInfo);

      if (!this.props.chatSession) {
        const talkSession = new Talk.Session({
          appId: talkjs_app_id,
          me: me,
          signature: user.talkjs_signature,
        });

        talkSession.unreads.on('change', (conversationIds) => {
          this.props.updateUnreadChats(conversationIds);
        });
        this.props.createTalkJSSession(talkSession);
      }
    });
  }

  loadPermissions = async () => {
    const user = this.props.user;

    await this.props.fetchPermissionsAction();
    const active_permissions = Object.values(this.props.permissions).filter(
      (permission) => {
        return permission.subscription_active;
      }
    );

    if (active_permissions.length === 0 && user.user.organization) {
      setTimeout(this.loadPermissions, 10 * 1000);
      return false;
    }
    return true;
  };

  initPushNotifications = async (user) => {
    if (user.user && this.state.userInitialized) {
      await initPushNotification(user.user.id);
    }
  };

  loadResources = async () => {
    const user = this.props.user;

    const { new_user, waitlisted, active, nomad_admin } = user.user;

    await this.props.fetchPermissionsAction();
    const hasDrmPermission = hasPermission(
      this.props.permissions,
      PERMISSION_NAME.DRM
    );

    if (
      new_user ||
      (waitlisted && !hasDrmPermission) ||
      !active ||
      nomad_admin
    ) {
      this.setState({ isBusy: false });
      // These users don't have full accounts and therefor nothing to load.
      return;
    }

    if (user.user.data_provider) {
      this.props
        .fetchAllProposals(false)
        .finally(() => setTimeout(() => this.setState({ isBusy: false })));
    } else if (
      hasRequestsPermission(this.props.permissions) ||
      isRequestViewerOnly(this.props.permissions)
    ) {
      this.props
        .fetchAllProposals(true)
        .finally(() => setTimeout(() => this.setState({ isBusy: false })));
    } else {
      this.setState({ isBusy: false });
    }
    if (!this.props.dataRefresh) {
      const interval = setInterval(this.loadResources, 60 * 1000);
      this.props.saveDataRefreshInterval(interval);
    }
    if (user.user && !this.state.userInitialized) {
      this.setState({ userInitialized: true });
      const has_active_permissions = await this.loadPermissions();
      if (process.env.NODE_ENV !== 'development') {
        datadogRum.setGlobalContext({
          user: {
            user_id: user.user.id,
            first_name: user.user.first_name,
            last_name: user.user.last_name,
            email: user.user.email,
          },
        });
      }
      let role = 'No Affiliation';
      if (user.user?.organization) {
        role = 'Buyer';
      } else if (user.user?.data_provider) {
        role = 'Seller';
      }

      const enterprise_subscriptions = Object.values(
        this.props.permissions
      ).filter((permission) => {
        return (
          permission?.plan_family === 'enterprise' &&
          permission?.subscription_active
        );
      });
      pendo.initialize({
        visitor: {
          id: user.user.email,
          email: user.user.email,
          full_name: `${user.user.first_name} ${user.user.last_name}`,
          role,
          has_subscription: has_active_permissions,
          enterprise_subscription:
            enterprise_subscriptions && enterprise_subscriptions.length > 0,
          subscription_plan_id:
            enterprise_subscriptions &&
            (enterprise_subscriptions.length > 0
              ? enterprise_subscriptions.map((s) => s?.plan_id)
              : null),
        },

        account: {
          id:
            user.user?.organization?.name ||
            user.user?.data_provider?.name ||
            'No Affiliation',
        },
      });
    }

    if (this.state.userInitialized && hasDrmPermission)
      loadBackendTranslations();

    this.initPushNotifications(user);
  };

  componentDidMount() {
    const user = this.props.user;
    if (process.env.NODE_ENV !== 'development') {
      datadogRum.init({
        applicationId: DATADOG_APPLICATION_ID,
        clientToken: DATADOG_CLIENT_TOKEN,
        site: DATADOG_SITE,
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        env: process.env.REACT_APP_DD_ENV,
        service: 'django',
        allowedTracingUrls: [process.env.REACT_APP_API_BACKEND],
      });
    }

    if (
      user &&
      !this.props.location.pathname.includes('/logout') &&
      !this.props.location.pathname.includes('/authorize') &&
      !this.props.location.pathname.includes('/direct-login') &&
      !this.props.location.pathname.includes('/direct-signup')
    ) {
      // Loading the below while logging out will cause an error since user object will be wiped
      // before the strapi API gets called
      this.props.fetchPersistedUser();
      this.loadResources();
    } else if (
      !user &&
      !this.props.location.pathname.includes('/authorize') &&
      !this.props.location.pathname.includes('/login') &&
      !this.props.location.pathname.includes('/proposal/token/')
    ) {
      this.props.history.push('/logout');
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.user?.ghostUserEmail &&
      !this.props.user?.user?.nomad_admin &&
      !this.props.user?.user?.ghosting_admin &&
      prevProps.user?.user?.nomad_admin &&
      prevProps.user?.user?.ghosting_admin
    ) {
      this.loadResources();
    }
  }

  processLogin = (routeProps) => {
    const user = this.props.user;
    this.loadResources();

    if (user.user.new_user) {
      routeProps.history.push(user.user.isInvited ? '/apply-invite' : '/apply');
    } else if (user.user.data_provider && !user.user.nomad_admin) {
      routeProps.history.push(
        routeProps?.location?.state?.from?.pathname ||
          '/data-requests/active/waiting-seller'
      );
    } else {
      routeProps.history.push(
        routeProps?.location?.state?.from?.pathname || '/'
      );
    }
  };

  renderDirectAuth0() {
    const { pathname, search } = this.props.location;
    const searchparams = new URLSearchParams(search);

    const { isLoading: auth0IsLoading } = this.props.auth0;
    if (!auth0IsLoading && pathname === '/direct-login') {
      this.props.auth0.loginWithRedirect();
      return null;
    } else if (pathname === '/direct-signup') {
      this.props.logoutUserAction().then(() => {
        this.props.auth0.logout();
        this.props.auth0.loginWithRedirect({
          screen_hint: 'signup',
          login_hint: searchparams.get('email') || '',
        });
      });
      return null;
    }
  }

  renderContent(user) {
    if (user?.user?.nomad_admin) {
      return (
        <>
          {user?.user?.ghosting_admin && (
            <Route path="/all-users" component={Users} />
          )}
          <Route path="/top-providers" component={TopProviders} />
          <Route path="/proposal-token" component={TokenResponseProposal} />
          {user?.user?.ghosting_admin && (
            <Route path="/accept-proposal" component={AcceptProposal} />
          )}
        </>
      );
    }
    return (
      <>
        {user.user?.organization && user.user?.waitlisted && (
          <CheckUserStatus />
        )}
        <Route path="/direct-login" render={this.renderDirectAuth0} />
        <Route path="/direct-signup" render={this.renderDirectAuth0} />
        {this.props.user?.user && !this.props.user?.user.active ? (
          <Redirect to="/inactive" />
        ) : (
          <>
            <ProtectedRoute
              exact
              path="/create"
              component={CreateSearchV2}
              noproviders
              nonewusers
            />
            <ProtectedRoute
              exact
              path="/edit/:id"
              component={EditSearch}
              noproviders
              nonewusers
            />
            <ProtectedRoute
              path={[
                '/requester/request-details/:id',
                '/reviewer/request-details/:id',
                '/viewer/request-details/:id',
              ]}
              component={SearchDetailsContainer}
              noproviders
              nonewusers
              permissions={[
                PERMISSION_NAME.CREATE_SEARCH,
                PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
                PERMISSION_NAME.CREATE_SEARCH_OWN,
                PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
                PERMISSION_NAME.REVIEW_SEARCH_ORG,
                PERMISSION_NAME.REVIEW_SEARCH_MASTER_ORG,
                PERMISSION_NAME.VIEW_SEARCHES_ORG,
                PERMISSION_NAME.VIEW_SEARCHES_MASTER_ORG,
                PERMISSION_NAME.CREATE_SEARCH_FREE,
              ]}
            />
            <ProtectedRoute
              path="/data-provider-details/:id"
              component={DataVendorDetailsContainer}
              noproviders
              nonewusers
              permissions={[PERMISSION_NAME.DRM]}
            />
            <ProtectedRoute
              path="/contacts/:page"
              component={Contacts}
              nonewusers
              permissions={[PERMISSION_NAME.DRM]}
            />
            <ProtectedRoute
              path="/contact-details/:id"
              component={ContactsDetailsContainer}
              noproviders
              nonewusers
              permissions={[PERMISSION_NAME.DRM]}
            />
            <FeatureToggle featureName={FEATURE_FLAGS.AI_THREAD}>
              <ProtectedRoute
                path="/ai-search"
                component={AISearchContainer}
                nonewusers
                permissions={[
                  PERMISSION_NAME.DOCUMENT_CHAT,
                  PERMISSION_NAME.DRM_CHAT,
                ]}
              />
            </FeatureToggle>
            <ProtectedRoute
              path={['/data-requests']}
              component={DataRequests}
              nobuyers
              nonewusers
            />
            <FeatureToggle featureName={FEATURE_FLAGS.SELLER_SEARCH_DETAILS}>
              <ProtectedRoute
                path={['/data-request-details/:id']}
                component={DataRequestDetailsContainer}
                nobuyers
                nonewusers
              />
            </FeatureToggle>
            <ProtectedRoute
              path={['/discussions/:proposalId', '/discussions']}
              component={Discussions}
              nobuyers
              nonewusers
            />
            <Route path={'/success'} component={SuccessFreeSearchSplash} />
            <ProtectedRoute
              path={'/requests'}
              component={SearchViewContainer}
              noproviders
              nonewusers
            />
            <ProtectedRoute
              path="/featured-data-providers/:page"
              component={FeaturedDataProvidersContainer}
              nonewusers
              permissions={[PERMISSION_NAME.FEATURED_PROVIDERS]}
            />

            <ProtectedRoute path="/settings" component={Settings} nonewusers />
            <ProtectedRoute
              path="/no-data-providers/home"
              component={DataVendorsHome}
              nonewusers
              permissions={[PERMISSION_NAME.DRM]}
            />
            <ProtectedRoute
              path="/data-providers/:page"
              component={DataVendors}
              nonewusers
              permissions={[PERMISSION_NAME.DRM]}
            />
            <ProtectedRoute
              path="/data-sets/:page"
              component={DataSets}
              nonewusers
              permissions={[PERMISSION_NAME.DRM]}
            />
            <ProtectedRoute
              path="/data-set-details/:id"
              component={DatasetDetailsContainer}
              noproviders
              nonewusers
              permissions={[PERMISSION_NAME.DRM]}
            />
            <ProtectedRoute exact path="/apply" component={SignUpFlow} />
            <ProtectedRoute
              exact
              path="/apply-invite"
              component={() => <SignUpInvite />}
            />
            <ProtectedRoute exact path="/waitlist" component={Waitlist} />
            <ProtectedRoute
              exact
              path="/file-library"
              component={FileLibrary}
            />
            <FeatureToggle featureName={FEATURE_FLAGS.FREE_CONNECT}>
              <ProtectedRoute
                path="/confirm-email"
                component={ConfirmEmailVerification}
              />
            </FeatureToggle>
          </>
        )}
        <ProtectedRoute exact path="/inactive" component={Inactive} />
      </>
    );
  }

  isLoadingUser() {
    return (
      (!this.props.user?.user &&
        !isPublicRoute(this.props.location.pathname)) ||
      (this.state.userInitialized &&
        !Object.keys(this.props?.permissions).length)
    );
  }

  isBusy() {
    return this.isLoadingUser() || this.state.isBusy;
  }

  handleRedirects() {
    const hasDrmPermission = hasPermission(
      this.props.permissions,
      PERMISSION_NAME.DRM
    );
    if (
      this.props.user.user.waitlisted &&
      !hasDrmPermission &&
      this.props.user.user.org_or_provider?.user_type !== 'data_buyer'
    ) {
      return <Redirect to={{ pathname: '/waitlist' }} />;
    }

    return null;
  }

  render() {
    const { pathname } = this.props.location;
    if (pathname === '/direct-login') {
      this.props.auth0.loginWithRedirect();
      return null;
    } else if (pathname === '/direct-signup') {
      this.props.auth0.logout();
      this.props.auth0.loginWithRedirect({ screen_hint: 'signup' });
    }

    return (
      <>
        <Suspense fallback={null}>
          <FeatureFlagProvider user={this.props.user} onLoad={this.initChat}>
            <NotificationProvider user={this.props.user}>
              <QueryClientProvider client={queryClient}>
                {!isPublicRoute(pathname) ? (
                  <FeatureFlagContext.Consumer>
                    {(featureFlag) => (
                      <div
                        className={classnames('rootContainer', {
                          hide: this.isLoadingUser() || !featureFlag.isReady,
                        })}
                      >
                        <div className="h-100 redesign">
                          <MainWrapper isReady={featureFlag.isReady}>
                            <Layout>
                              {!this.isBusy() ? (
                                <>
                                  {this.renderContent(this.props.user)}
                                  {pathname === '/' ? (
                                    <Redirect
                                      to={getDefaultUrlPerRole(
                                        this.props.user.user,
                                        this.props.permissions
                                      )}
                                    />
                                  ) : null}
                                  {this.handleRedirects(pathname)}
                                </>
                              ) : null}
                            </Layout>
                          </MainWrapper>
                        </div>
                      </div>
                    )}
                  </FeatureFlagContext.Consumer>
                ) : null}
                <Route
                  exact
                  path="/proposal/token/:tokenId/:userId"
                  component={ProposalResponseToken}
                />
                <Route exact path="/login" render={() => <Login />} />
                <Route exact path="/logout" component={Logout} />
                <Route
                  path="/authorize"
                  render={(routeProps) => (
                    <Validate
                      onLogin={() => this.processLogin(routeProps)}
                      location={routeProps.location}
                      history={routeProps.history}
                    />
                  )}
                />
              </QueryClientProvider>
            </NotificationProvider>
            <ToastContainer />
          </FeatureFlagProvider>
        </Suspense>

        {this.isLoadingUser() ? (
          <div className="loadingIndicatorContainer">
            <Spinner animation="border" />
          </div>
        ) : null}

        {this.props.user?.ghostUserEmail ? (
          <GhostButton ghostUser={this.props.ghostUser} />
        ) : null}
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    chats: state.chats,
    user: state.user,
    permissions: state.permissions.permissions,
    chatSession: state.chat.chatSession,
    dataRefresh: state.intervals.dataRefresh,
  };
};

export default connect(mapStateToProps, {
  fetchConversations,
  fetchPersistedUser,
  createTalkJSSession,
  updateUnreadChats,
  fetchAllProposals,
  saveDataRefreshInterval,
  logoutUserAction,
  ghostUser,
  fetchPermissionsAction,
})(withAuth0(withRouter(App)));
